import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Truck, Leaf, Users, CheckCircle2, Star, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { ProductCard } from '@/components/products/ProductCard';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Home() {
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const { addToCart } = useCart();
  const { user } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Seed data first
        await axios.post(`${API}/seed`);
        
        const [productsRes, reviewsRes] = await Promise.all([
          axios.get(`${API}/products`),
          axios.get(`${API}/reviews`)
        ]);
        setProducts(productsRes.data.slice(0, 4));
        setReviews(reviewsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const handleAddToCart = async (product) => {
    if (!user) {
      toast.error('Please sign in to add items to cart');
      return;
    }
    try {
      await addToCart(product.product_id, 1, 'one-time');
      toast.success(`${product.name} added to cart!`);
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  return (
    <div className="pt-20" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-cream" data-testid="hero-section">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1758546517190-2de09f5cd805?crop=entropy&cs=srgb&fm=jpg&w=1920&q=80"
            alt="Fresh harvest"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-cream via-cream/95 to-cream/70"></div>
        </div>

        <div className="container-custom relative z-10 py-24">
          <div className="max-w-3xl">
            <span className="inline-block badge-local mb-6 animate-fade-in-up">
              Serving Coös County
            </span>
            
            <h1 className="font-playfair text-5xl md:text-7xl lg:text-[5.5rem] font-bold text-dark leading-[1.1] tracking-tight mb-8 animate-fade-in-up animation-delay-100" data-testid="hero-title">
              Fresh, Local Farm Boxes
              <span className="block text-forest">Delivered to You</span>
            </h1>
            
            <p className="font-manrope text-lg md:text-xl text-dark/70 leading-relaxed mb-10 max-w-xl animate-fade-in-up animation-delay-200">
              Support local farms. Eat better. Enjoy peak-fresh produce, dairy, meat, and seasonal boxes delivered right to your door.
            </p>

            <div className="flex flex-wrap gap-4 animate-fade-in-up animation-delay-300">
              <Link to="/shop" className="btn-primary" data-testid="hero-shop-btn">
                Browse Boxes
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <Link to="/farms" className="btn-secondary" data-testid="hero-farms-btn">
                Meet Our Farms
              </Link>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap gap-8 mt-12 pt-8 border-t border-forest/10 animate-fade-in-up animation-delay-400">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-forest" />
                <span className="font-manrope text-sm text-dark/70">100% Local Farms</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-forest" />
                <span className="font-manrope text-sm text-dark/70">Free delivery over $50</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-forest" />
                <span className="font-manrope text-sm text-dark/70">Cancel anytime</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding bg-white" data-testid="how-it-works-section">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-xs font-manrope uppercase tracking-[0.2em] font-bold text-forest mb-4 block">Simple & Easy</span>
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-dark">How FarmBox Works</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {[
              {
                step: '01',
                icon: Leaf,
                title: 'Choose Your Box',
                description: 'Pick from fresh vegetables, fruits, dairy, meat, or mixed seasonal boxes.'
              },
              {
                step: '02',
                icon: Users,
                title: 'Pick Your Schedule',
                description: 'One-time, weekly, biweekly, or monthly delivery \u2014 cancel anytime.'
              },
              {
                step: '03',
                icon: Truck,
                title: 'Delivered Fresh',
                description: 'Straight from local farms in the White Mountain Region to your door.'
              }
            ].map((item, index) => (
              <div key={index} className="relative group">
                <div className="bg-cream/50 rounded-[2rem] p-8 h-full border border-forest/5 hover:border-forest/20 transition-all duration-300 hover:shadow-ambient">
                  <span className="font-caveat text-6xl text-gold/30 absolute top-4 right-6">{item.step}</span>
                  <div className="w-16 h-16 bg-forest/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-forest group-hover:scale-105 transition-all duration-300">
                    <item.icon className="w-8 h-8 text-forest group-hover:text-cream transition-colors" />
                  </div>
                  <h3 className="font-playfair text-2xl font-semibold text-dark mb-3">{item.title}</h3>
                  <p className="font-manrope text-dark/60 leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="section-padding bg-cream" data-testid="featured-products-section">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <span className="text-xs font-manrope uppercase tracking-[0.2em] font-bold text-forest mb-4 block">Fresh Picks</span>
              <h2 className="font-playfair text-4xl md:text-5xl font-bold text-dark">Popular Boxes</h2>
            </div>
            <Link to="/shop" className="flex items-center gap-2 text-forest font-manrope font-medium hover:gap-3 transition-all" data-testid="view-all-products">
              View All Boxes
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {products.map((product) => (
              <ProductCard key={product.product_id} product={product} onAddToCart={handleAddToCart} />
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-dark relative overflow-hidden noise-overlay" data-testid="why-choose-section">
        <div className="container-custom relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-xs font-manrope uppercase tracking-[0.2em] font-bold text-gold mb-4 block">Why FarmBox</span>
              <h2 className="font-playfair text-4xl md:text-5xl font-bold text-cream mb-8">Why Choose FarmBox?</h2>
              
              <div className="space-y-6">
                {[
                  { title: '100% Local Farms', desc: 'Every box supports small New Hampshire farmers.' },
                  { title: 'Peak Freshness', desc: 'Picked the same morning it\'s delivered.' },
                  { title: 'Flexible Subscriptions', desc: 'No commitments, skip or cancel anytime.' },
                  { title: 'Transparent Sourcing', desc: 'Know exactly where your food comes from.' },
                  { title: 'Sustainable Practices', desc: 'Better for you, better for the planet.' },
                  { title: 'Refer & Earn', desc: 'Share your link — get 10% off + a free local product when a friend subscribes.' }
                ].map((item, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="w-6 h-6 rounded-full bg-forest flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle2 className="w-4 h-4 text-cream" />
                    </div>
                    <div>
                      <h4 className="font-manrope font-bold text-cream mb-1">{item.title}</h4>
                      <p className="font-manrope text-cream/60">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="rounded-[2rem] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1635341083388-84ca0a1733ca?w=800"
                  alt="Fresh produce box"
                  className="w-full aspect-square object-cover"
                />
              </div>
              <div className="absolute -bottom-8 -left-8 bg-gold rounded-2xl p-6 shadow-lg" data-testid="subscriber-discount-badge">
                <p className="font-caveat text-3xl text-dark mb-1">New Subscribers Get</p>
                <p className="font-manrope font-bold text-dark text-xl">10% Off Your First Order</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Recruit Farms Banner */}
      <section className="py-14 bg-forest relative overflow-hidden" data-testid="recruit-farms-banner">
        <div className="container-custom relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <span className="inline-block badge-local mb-3">Now Onboarding in Coos County</span>
            <h2 className="font-playfair text-2xl md:text-3xl font-bold text-cream mb-2">Are you an NH farmer or producer?</h2>
            <p className="font-manrope text-cream/70 max-w-xl">
              We're building the FarmBox roster one verified farm at a time. Join us and get your products in front of local households across the White Mountain Region.
            </p>
          </div>
          <Link
            to="/for-farms"
            className="flex-shrink-0 inline-flex items-center gap-2 px-8 py-4 bg-gold text-dark rounded-full font-manrope font-bold hover:bg-cream transition-colors"
            data-testid="recruit-farms-cta"
          >
            Join the Roster
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Customer Reviews */}
      <section className="section-padding bg-cream" data-testid="reviews-section">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-xs font-manrope uppercase tracking-[0.2em] font-bold text-forest mb-4 block">Customer Love</span>
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-dark">What Our Customers Say</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {reviews.map((review, index) => (
              <div key={review.review_id} className="bg-white rounded-[1.5rem] p-8 border border-forest/10 shadow-card">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className={`w-5 h-5 ${i < review.rating ? 'text-gold fill-gold' : 'text-dark/20'}`} />
                  ))}
                </div>
                <p className="font-manrope text-dark/80 leading-relaxed mb-6 italic">
                  "{review.comment}"
                </p>
                <p className="font-manrope font-bold text-dark">— {review.user_name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-forest relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-gold rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-cream rounded-full blur-3xl"></div>
        </div>
        
        <div className="container-custom relative z-10 text-center">
          <h2 className="font-playfair text-4xl md:text-5xl lg:text-6xl font-bold text-cream mb-6">
            Ready for Fresh, Local Goodness?
          </h2>
          <p className="font-manrope text-lg text-cream/80 mb-10 max-w-2xl mx-auto">
            Join hundreds of families in the White Mountain Region enjoying farm-fresh boxes every week.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/shop" className="btn-accent" data-testid="cta-shop-btn">
              Start Shopping
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
            <Link to="/delivery" className="inline-flex items-center justify-center bg-transparent border-2 border-cream text-cream rounded-full px-8 py-4 font-manrope font-bold hover:bg-cream hover:text-forest transition-all duration-300">
              Learn About Delivery
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
